import { useEffect, useMemo, useState } from 'react';
import { actionStatusLabel, isActionOverdue } from '../lib/actions';
import { loadBuilderActionList } from '../services/actions';
import type { ActionListItem, ActionStatus } from '../types/actions';
import type { IssuePriority } from '../types/issues';

interface BuilderActionListProps {
  ownerId: string;
  assessmentId: string;
  onBack: () => void;
  onOpenAction: (issueId: string) => void;
}

type StatusFilter = 'all' | 'drafts' | 'sent' | 'active' | 'rectification';
type PriorityFilter = 'all' | IssuePriority;

function inStatusFilter(action: ActionListItem, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'drafts') return action.status === 'draft' || action.status === 'ready_to_submit';
  if (filter === 'sent') return action.status === 'submitted' || action.status === 'acknowledged';
  if (filter === 'active') return action.status === 'in_progress' || action.status === 'disputed';
  return action.status === 'rectification_claimed';
}

function builderPackText(actions: ActionListItem[]): string {
  const lines = ['NivasaSaathi builder action list', ''];
  actions.forEach((action, index) => {
    lines.push(`${index + 1}. ${action.issue_title}`);
    lines.push(`Location: ${action.issue_location}`);
    lines.push(`Priority: ${action.priority}`);
    lines.push(`Requested correction: ${action.requested_action}`);
    lines.push(`Due date: ${action.due_date}`);
    lines.push(`Status: ${actionStatusLabel(action.status)}`);
    lines.push('');
  });
  lines.push('Buyer-recorded observations and action requests. This list is not a certification of defect, compliance or rectification.');
  return lines.join('\n');
}

const statuses: StatusFilter[] = ['all', 'drafts', 'sent', 'active', 'rectification'];
const priorities: PriorityFilter[] = ['all', 'immediate', 'urgent', 'important', 'monitor', 'minor'];

export function BuilderActionList({ ownerId, assessmentId, onBack, onOpenAction }: BuilderActionListProps) {
  const [actions, setActions] = useState<ActionListItem[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setStatus('loading');
      try {
        const next = await loadBuilderActionList(ownerId, assessmentId);
        if (!cancelled) {
          setActions(next);
          setStatus('ready');
        }
      } catch (caught) {
        if (!cancelled) {
          setStatus('error');
          setMessage(caught instanceof Error ? caught.message : 'Could not load builder actions.');
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [assessmentId, ownerId]);

  const visibleActions = useMemo(
    () => actions.filter((action) =>
      inStatusFilter(action, statusFilter) &&
      (priorityFilter === 'all' || action.priority === priorityFilter),
    ),
    [actions, priorityFilter, statusFilter],
  );

  const submittedCount = actions.filter((action) => ['submitted', 'acknowledged', 'in_progress', 'rectification_claimed'].includes(action.status)).length;
  const overdueCount = actions.filter((action) => isActionOverdue(action.due_date, action.status)).length;

  async function copyAll() {
    if (!visibleActions.length) return;
    try {
      await navigator.clipboard.writeText(builderPackText(visibleActions));
      setMessage('Visible builder actions copied. Send them through your chosen channel.');
    } catch {
      setMessage('Browser blocked copying. You can still review each action on this screen.');
    }
  }

  return (
    <main className="page builder-action-page">
      <button className="text-button" type="button" onClick={onBack}>← Back to findings</button>

      <section className="builder-action-hero">
        <span className="eyebrow">Builder action list</span>
        <h1>Corrections you want followed up</h1>
        <p className="lead">A practical list of buyer-recorded findings that have been converted into actions. Copy the list for external sharing without pretending NivasaSaathi sent it.</p>
        <div className="issue-stat-grid">
          <div><strong>{actions.length}</strong><span>Total actions</span></div>
          <div><strong>{submittedCount}</strong><span>Submitted / active</span></div>
          <div><strong>{overdueCount}</strong><span>Overdue</span></div>
          <div><strong>{actions.filter((action) => action.status === 'rectification_claimed').length}</strong><span>Rectification claimed</span></div>
        </div>
        <button className="button button-primary" type="button" disabled={visibleActions.length === 0} onClick={() => void copyAll()}>
          Copy visible action list
        </button>
      </section>

      {message ? <div className="status-banner" role="status">{message}</div> : null}
      {status === 'loading' ? <div className="status-banner">Loading actions…</div> : null}

      <section className="builder-filter-card">
        <div>
          <span className="eyebrow">Status</span>
          <div className="issue-filters">
            {statuses.map((value) => (
              <button key={value} type="button" className={`filter-chip ${statusFilter === value ? 'filter-chip-active' : ''}`} onClick={() => setStatusFilter(value)}>
                {value}
              </button>
            ))}
          </div>
        </div>
        <div>
          <span className="eyebrow">Priority</span>
          <div className="issue-filters">
            {priorities.map((value) => (
              <button key={value} type="button" className={`filter-chip ${priorityFilter === value ? 'filter-chip-active' : ''}`} onClick={() => setPriorityFilter(value)}>
                {value}
              </button>
            ))}
          </div>
        </div>
      </section>

      {status === 'ready' && visibleActions.length === 0 ? (
        <section className="empty-findings"><strong>No actions in this view.</strong><p>Convert a finding into an action from the issue register.</p></section>
      ) : null}

      <section className="builder-action-list">
        {visibleActions.map((action) => (
          <article className="builder-action-row" key={action.id}>
            <div className="issue-row-topline">
              <span className={`priority-chip priority-${action.priority}`}>{action.priority}</span>
              <span className="act-status-chip">{actionStatusLabel(action.status as ActionStatus)}</span>
              {isActionOverdue(action.due_date, action.status) ? <span className="overdue-chip">Overdue</span> : null}
            </div>
            <h2>{action.issue_title}</h2>
            <p>{action.requested_action}</p>
            <div className="action-list-meta">
              <span>{action.issue_location}</span>
              <span>Due {action.due_date}</span>
              <span>{action.accountable_party_name || action.accountable_party}</span>
            </div>
            <div className="issue-action-row">
              <button
                className="button button-secondary ink-button"
                type="button"
                onClick={() => onOpenAction(action.issue_id)}
              >
                Manage action
              </button>
            </div>
          </article>
        ))}
      </section>
    </main>
  );
}
